<script setup lang="ts">
import { NegotiationState } from '~/apiClient';
import type { AuctionItemEntityWithNegotiation } from '~/types/sale.type';

// PROPS
const { auction } = defineProps({
    auction: { type: Object as PropType<AuctionItemEntityWithNegotiation>, required: true },
});

// MODEL
const isModalOpen = defineModel<boolean>({ required: true });

// COMPOSABLES
const { isMobile } = useResponsive();
const { tt, tn } = useTypedI18n();
const { negotiation, buyerStopProposals, buyerStopProposalsIsPending } = useAuctionNegotiation(() => auction);

// METHODS
const closeModal = (): void => {
    isModalOpen.value = false;
};

const onConfirm = async (): Promise<void> => {
    try {
        await buyerStopProposals();
        closeModal();
    } catch {}
};

// WATCHERS
watch(
    () => negotiation.value.state,
    value => isModalOpen.value && value !== NegotiationState.Open && closeModal()
);
</script>

<template>
    <UModal
        v-model:open="isModalOpen"
        :fullscreen="isMobile"
        :title="tt('auctionNegotiation.buyerStopProposalsModal.title')"
        v-bind="modalNoDescAriaFixupProps"
    >
        <template #body>
            <div class="space-y-6 text-center">
                <p class="text-dimmed text-xs lg:text-sm">
                    {{ tt('auctionNegotiation.buyerStopProposalsModal.disclaimer') }}
                </p>
                <div>
                    <p class="text-dimmed text-xs lg:text-sm">
                        {{ tt('auctionNegotiation.yourOffer') }}
                    </p>
                    <p class="text-primary-500 text-2xl font-semibold lg:text-3xl">{{ tn(negotiation.currentBuyerProposal.amount, 'currency') }}</p>
                    <SaleFees
                        :reference-date="auction.startAt"
                        :base-amount="negotiation.currentBuyerProposal.amount"
                        :assets-count="auction.assets.length"
                        :fee-configuration="auction.userMeta?.feeOverride"
                        is-buyer
                    />
                </div>
            </div>
        </template>
        <template #footer="{ close }">
            <UButton
                size="xl"
                :label="tt('auctionNegotiation.buyerStopProposalsModal.confirmButton')"
                block
                :loading="buyerStopProposalsIsPending"
                @click="onConfirm"
            />
            <UButton size="xl" block color="neutral" variant="link" :label="tt('auctionNegotiation.cancel')" @click="close" />
        </template>
    </UModal>
</template>
